import { supabase } from "@/lib/supabase";

export type CalendarConnection = {
  google_email?: string | null;
  calendar_id?: string;
  sync_deadlines?: boolean;
  sync_interviews?: boolean;
  connected_at?: string;
  last_synced_at?: string | null;
  updated_at?: string;
};

export type CalendarEventRow = {
  id: string;
  event_type: "deadline" | "interview";
  title?: string | null;
  event_start?: string | null;
  program_id?: string | null;
  application_id?: string | null;
  google_event_id?: string;
  meet_link?: string | null;
};

type CalendarApiResult = {
  ok: boolean;
  error?: string;
  message?: string;
  connected?: boolean;
  connection?: CalendarConnection | null;
  events?: CalendarEventRow[];
  url?: string;
  redirect_uri?: string;
  google_email?: string;
  synced?: number;
  event_id?: string;
};

async function invokeCalendar(body: Record<string, unknown>): Promise<CalendarApiResult> {
  const { data, error } = await supabase.functions.invoke("partner-google-calendar", {
    body,
  });

  if (error) {
    // functions.invoke may put JSON error body on context
    const ctx = (error as { context?: Response }).context;
    if (ctx) {
      try {
        const parsed = await ctx.json();
        return parsed as CalendarApiResult;
      } catch {
        /* fall through */
      }
    }
    return { ok: false, error: "invoke_failed", message: error.message };
  }

  return (data || { ok: false, error: "empty_response" }) as CalendarApiResult;
}

export async function getCalendarStatus() {
  return invokeCalendar({ action: "status" });
}

export async function startGoogleCalendarConnect() {
  return invokeCalendar({
    action: "get_auth_url",
    redirect_origin: window.location.origin,
  });
}

export async function completeGoogleCalendarConnect(code: string, state: string) {
  return invokeCalendar({
    action: "exchange_code",
    code,
    state,
    redirect_origin: window.location.origin,
  });
}

export async function disconnectGoogleCalendar() {
  return invokeCalendar({ action: "disconnect" });
}

export async function syncAllCalendarDeadlines() {
  return invokeCalendar({ action: "sync_all" });
}

export async function syncProgramDeadline(programId: string) {
  return invokeCalendar({ action: "sync_program", program_id: programId });
}

export async function syncInterviewEvent(applicationId: string) {
  return invokeCalendar({ action: "sync_interview", application_id: applicationId });
}

export async function updateCalendarPrefs(prefs: {
  sync_deadlines?: boolean;
  sync_interviews?: boolean;
}) {
  const { data, error } = await supabase.rpc("update_partner_calendar_prefs", {
    p_sync_deadlines: prefs.sync_deadlines ?? null,
    p_sync_interviews: prefs.sync_interviews ?? null,
  });
  if (error) return { ok: false, error: error.message };
  return (data || { ok: true }) as { ok: boolean; error?: string };
}

/** One-click Google Calendar template URL (no OAuth). */
export function googleCalendarDeadlineUrl(opts: {
  title: string;
  deadline: string;
  details?: string;
  location?: string;
}) {
  const day = opts.deadline.slice(0, 10).replace(/-/g, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Deadline: ${opts.title}`,
    dates: `${day}/${day}`,
    details: opts.details || "Skutopia program application deadline",
  });
  if (opts.location) params.set("location", opts.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
